import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { type ReceiptDto, type ReceiptSource, vnDate } from "@hisense/shared";
import { useReceiptWarehouses } from "@/api/warehouseReceipts";
import { ApiClientError, apiFetch } from "@/lib/api";
import { formatQty } from "@/domain/money";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { LotLabelsButton } from "@/components/warehouse/LotLabelsButton";
import { useWarehouseAccess } from "@/components/warehouse/WarehouseRules";
import { ScanField, cleanQuantity, locationFromScan, scanError } from "./ScanField";

const today = () => vnDate();
type Line = { quantity: string; lot: string; location: string };

/**
 * Nhận hàng theo đơn mua: quét / gõ số đơn mua → nhập số nhận, số lô, vị trí từng dòng → lập phiếu nhập và xác nhận → in
 * tem lô dán lên hàng. Dùng đúng API của trang Phiếu nhập kho (cùng kiểm tra, cùng IQC sau đó). Xác nhận cần quyền Duyệt ở
 * Kho & LOT — không có thì phiếu nằm nháp chờ người duyệt, màn này nói rõ.
 */
export function ReceiveTask() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canConfirm = useWarehouseAccess()("approve");
  const warehouses = useReceiptWarehouses();
  const [source, setSource] = useState<ReceiptSource | null>(null);
  const [lines, setLines] = useState<Map<string, Line>>(new Map());
  const [warehouseId, setWarehouseId] = useState("");
  const [receivedDate, setReceivedDate] = useState(today());
  const [deliveryNote, setDeliveryNote] = useState("");
  const [receipt, setReceipt] = useState<ReceiptDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const list = warehouses.data?.data ?? [];
    if (!warehouseId && list.length) setWarehouseId((list.find((w) => w.code === "WH-SG") ?? list[0]!).id);
  }, [warehouses.data, warehouseId]);

  const openPo = async (raw: string) => {
    setError(null);
    setNotice(null);
    setSource(null);
    setReceipt(null);
    setBusy(true);
    try {
      const choices = await apiFetch<{ data: { id: string; poNumber: string }[] }>("/warehouse-receipts/sources", { query: { q: raw } });
      const hit = choices.data.find((c) => c.poNumber.trim().toLowerCase() === raw.trim().toLowerCase());
      if (!hit) return setError(t("scan.receive.notFound", { no: raw }));
      const res = await apiFetch<{ data: ReceiptSource }>(`/warehouse-receipts/sources/${hit.id}`);
      setSource(res.data);
      setLines(new Map(res.data.lines.filter((l) => Number(l.remainingQuantity) > 0)
        .map((l) => [l.id, { quantity: l.remainingQuantity, lot: l.suggestedLot ?? "", location: l.defaultLocation ?? "" }])));
    } catch (e) {
      setError(scanError(e, t, raw));
    } finally {
      setBusy(false);
    }
  };
  const patch = (id: string, p: Partial<Line>) => setLines((prev) => new Map(prev).set(id, { ...prev.get(id)!, ...p }));
  // Ô số nhận để trống hoặc 0 = dòng không nhận lần này. Gõ sai ("1,5", "12 cái") thì báo, KHÔNG lặng lẽ bỏ dòng đó ra khỏi
  // phiếu nhập (người nhận tưởng đã nhập mà tồn không tăng).
  const badQuantity = source?.lines.filter((l) => { const q = lines.get(l.id)?.quantity.trim() ?? ""; return q !== "" && !cleanQuantity(q); }) ?? [];
  const chosen = source?.lines.filter((l) => lines.has(l.id) && cleanQuantity(lines.get(l.id)!.quantity) && Number(lines.get(l.id)!.quantity) > 0) ?? [];
  const ready = !!source && !!warehouseId && deliveryNote.trim().length > 0 && chosen.length > 0 && !badQuantity.length
    && chosen.every((l) => { const v = lines.get(l.id)!; return v.lot.trim() && locationFromScan(v.location); });

  const submit = async () => {
    if (!source || !ready || busy) return;
    setBusy(true);
    setError(null);
    let draft: ReceiptDto | null = null;
    try {
      const res = await apiFetch<{ data: ReceiptDto }>("/warehouse-receipts", {
        method: "POST",
        body: { requestId: crypto.randomUUID(), poId: source.id, warehouseId, receivedDate, deliveryNote: deliveryNote.trim(), notes: "",
          lines: chosen.map((l) => { const v = lines.get(l.id)!; return { id: crypto.randomUUID(), poLineId: l.id, lotNumber: v.lot.trim(), quantity: v.quantity.trim(), location: locationFromScan(v.location) }; }) },
      });
      draft = res.data;
      // Xác nhận nhập kho cần quyền Duyệt ở Kho & LOT: không có thì dừng ở phiếu nháp, xoá màn để không lập phiếu trùng.
      if (!canConfirm) {
        setError(t("scan.receive.draftOnly", { no: draft.receiptNo }));
        setSource(null);
        await qc.invalidateQueries({ queryKey: ["receipts"] });
        return;
      }
      const done = await apiFetch<{ data: ReceiptDto }>(`/warehouse-receipts/${draft.id}/confirm`, { method: "POST", body: { revision: draft.revision } });
      setReceipt(done.data);
      setNotice(t("scan.receive.done", { no: done.data.receiptNo }));
      setSource(null);
      await Promise.all([qc.invalidateQueries({ queryKey: ["receipts"] }), qc.invalidateQueries({ queryKey: ["dashboard"] })]);
    } catch (e) {
      if (draft && e instanceof ApiClientError && e.status === 403) {
        setError(t("scan.receive.draftOnly", { no: draft.receiptNo }));
        setSource(null);
      } else setError(scanError(e, t, ""));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <ScanField id="scan-receive-po" label={t("scan.receive.scanPo")} placeholder={t("scan.receive.poPlaceholder")} busy={busy} autoFocus={!source} onScan={(v) => void openPo(v)} />
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      {notice && (
        <div role="status" className="space-y-2 rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">
          <p>{notice}</p>
          {receipt && <LotLabelsButton lots={receipt.lines.map((l) => ({ productCode: l.productCode, subRef: l.subRef, description: l.description, lotNumber: l.lotNumber }))} size="md" />}
        </div>
      )}
      {source && (
        <div className="space-y-3">
          <p className="text-sm"><span className="font-mono font-semibold">{source.poNumber}</span> · {source.supplierName}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <FieldLabel htmlFor="scan-receive-wh">{t("scan.receive.warehouse")}</FieldLabel>
              <Select id="scan-receive-wh" containerClassName="w-full" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                {warehouses.data?.data.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
              </Select>
            </div>
            <div>
              <FieldLabel htmlFor="scan-receive-date">{t("scan.receive.date")}</FieldLabel>
              <Input id="scan-receive-date" type="date" className="h-12" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} />
            </div>
            <div>
              <FieldLabel htmlFor="scan-receive-dn">{t("scan.receive.deliveryNote")}</FieldLabel>
              <Input id="scan-receive-dn" className="h-12 font-mono" value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} invalid={!deliveryNote.trim()} />
            </div>
          </div>
          <ul className="space-y-2">
            {source.lines.filter((l) => lines.has(l.id)).map((l) => {
              const v = lines.get(l.id)!;
              return (
                <li key={l.id} className="space-y-2 rounded-md border border-line bg-surface p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-mono text-sm font-semibold">{l.productCode}{l.subRef && <span className="text-ink-soft"> / {l.subRef}</span>}</p>
                    <p className="text-xs text-ink-soft">{t("scan.receive.remaining")} <span className="font-mono tabular">{formatQty(Number(l.remainingQuantity))}</span> {l.unit}</p>
                  </div>
                  {l.description && <p className="text-xs text-ink-soft">{l.description}</p>}
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Input className="h-10 font-mono" inputMode="decimal" aria-label={`${t("scan.receive.quantity")} ${l.productCode}`} placeholder={t("scan.receive.quantity")} value={v.quantity}
                      invalid={v.quantity.trim() !== "" && !cleanQuantity(v.quantity)} onChange={(e) => patch(l.id, { quantity: e.target.value })} />
                    <Input className="h-10 font-mono" aria-label={`${t("scan.receive.lot")} ${l.productCode}`} placeholder={t("scan.receive.lot")} value={v.lot} onChange={(e) => patch(l.id, { lot: e.target.value })} />
                    <Input className="h-10 font-mono" aria-label={`${t("scan.receive.location")} ${l.productCode}`} placeholder={t("scan.receive.location")} value={v.location} onChange={(e) => patch(l.id, { location: e.target.value })} />
                  </div>
                </li>
              );
            })}
          </ul>
          {!deliveryNote.trim() && <p className="text-sm text-ink-soft">{t("scan.receive.needDeliveryNote")}</p>}
          {badQuantity.length > 0 && <p role="alert" className="text-sm text-danger">{t("scan.receive.badQuantity", { codes: badQuantity.map((l) => l.productCode).join(", ") })}</p>}
          {!chosen.length && !badQuantity.length && <p className="text-sm text-ink-soft">{t("scan.receive.nothing")}</p>}
          {!canConfirm && <p className="text-sm text-ink-soft">{t("scan.receive.draftHint")}</p>}
          <Button className="h-12 w-full" disabled={!ready || busy} onClick={() => void submit()}>{t(canConfirm ? "scan.receive.submit" : "scan.receive.saveDraft")}</Button>
        </div>
      )}
    </div>
  );
}
